import React, { useState, useEffect } from 'react';
import './UpdateForm.css';

const NUM_BOXES = 8;

const makeEmptyBox = () => ({
  ccEmails: '',
  bccEmails: '',
  attachment1: '',
  attachment2: '',
  attachment3: '',
  subjectLine: '',
  body: '',
});

// Guards against corrupted localStorage data from the old buggy version
// (where ccEmails/bccEmails could have been overwritten from arrays to strings,
// or boxes could have been sharing a single object reference).
const sanitizeFormData = (data) => {
  if (!Array.isArray(data) || data.length !== NUM_BOXES) {
    return null;
  }
  return data.map((box) => {
    const safeBox = { ...makeEmptyBox(), ...(box || {}) };
    // Normalize ccEmails/bccEmails to a plain string regardless of
    // whether old data left them as an array or a string.
    if (Array.isArray(safeBox.ccEmails)) {
      safeBox.ccEmails = safeBox.ccEmails.join(', ');
    }
    if (Array.isArray(safeBox.bccEmails)) {
      safeBox.bccEmails = safeBox.bccEmails.join(', ');
    }
    return safeBox;
  });
};

const UpdateForm = ({ onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(0);
  // Independent objects per box — NOT Array(8).fill({...}), which points
  // every box at the same shared object and causes cross-box data corruption.
  const [formData, setFormData] = useState(
    Array.from({ length: NUM_BOXES }, () => makeEmptyBox())
  );

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert the comma-separated email strings into arrays only at submit time,
    // trimming whitespace and dropping empty entries.
    const outgoingData = formData.map((box) => ({
      ...box,
      ccEmails: box.ccEmails
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean),
      bccEmails: box.bccEmails
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean),
    }));

    onSubmit(outgoingData); // Pass data to the parent component
    localStorage.setItem('formData', JSON.stringify(formData));
  };

  // Load and sanitize saved data when the component mounts
  useEffect(() => {
    try {
      const savedData = JSON.parse(localStorage.getItem('formData'));
      const safeData = sanitizeFormData(savedData);
      if (safeData) {
        setFormData(safeData);
      } else if (savedData) {
        // Data existed but was in a shape we don't recognize — drop it
        // rather than let it crash the form again.
        localStorage.removeItem('formData');
      }
    } catch (err) {
      console.error('Failed to load saved form data, resetting.', err);
      localStorage.removeItem('formData');
    }
  }, []);

  // Handle Next and Previous steps
  const handleNext = () => {
    if (currentStep < NUM_BOXES - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle form field change for each box — creates new objects instead of
  // mutating shared state in place.
  const handleChange = (e, field, boxIndex) => {
    const { value } = e.target;
    setFormData((prevFormData) =>
      prevFormData.map((box, idx) =>
        idx === boxIndex ? { ...box, [field]: value } : box
      )
    );
  };

  return (
    <div className="form-container">
      <h2 className="form-title">Generate Excel Booking & Calendar</h2>
      <form onSubmit={handleSubmit} className="update-form">
        <div className="form-step">
          {/* Render each box dynamically */}
          {formData.map((boxData, index) => (
            currentStep === index && (
              <div className="step-box" key={index}>
                <h3>Box {index + 1}</h3>
                <div className="form-group">
                  <label htmlFor={`ccEmails-${index}`}>CC Emails</label>
                  <input
                    type="text"
                    id={`ccEmails-${index}`}
                    value={boxData.ccEmails}
                    onChange={(e) => handleChange(e, 'ccEmails', index)}
                    placeholder="Enter CC Emails (separate with commas)"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`bccEmails-${index}`}>BCC Emails</label>
                  <input
                    type="text"
                    id={`bccEmails-${index}`}
                    value={boxData.bccEmails}
                    onChange={(e) => handleChange(e, 'bccEmails', index)}
                    placeholder="Enter BCC Emails (separate with commas)"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`attachment1-${index}`}>Attachment1</label>
                  <input
                    type="text"
                    id={`attachment1-${index}`}
                    value={boxData.attachment1}
                    onChange={(e) => handleChange(e, 'attachment1', index)}
                    placeholder="Enter Attachment1"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`attachment2-${index}`}>Attachment2</label>
                  <input
                    type="text"
                    id={`attachment2-${index}`}
                    value={boxData.attachment2}
                    onChange={(e) => handleChange(e, 'attachment2', index)}
                    placeholder="Enter Attachment2"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`attachment3-${index}`}>Attachment3</label>
                  <input
                    type="text"
                    id={`attachment3-${index}`}
                    value={boxData.attachment3}
                    onChange={(e) => handleChange(e, 'attachment3', index)}
                    placeholder="Enter Attachment3"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`subjectLine-${index}`}>Subject Line</label>
                  <input
                    type="text"
                    id={`subjectLine-${index}`}
                    value={boxData.subjectLine}
                    onChange={(e) => handleChange(e, 'subjectLine', index)}
                    placeholder="Enter Subject Line"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`body-${index}`}>Body</label>
                  <textarea
                    id={`body-${index}`}
                    value={boxData.body}
                    onChange={(e) => handleChange(e, 'body', index)}
                    placeholder="Enter Body Text"
                  />
                </div>
              </div>
            )
          ))}
        </div>

        <div className="form-navigation">
          <button
            type="button"
            className="prev-btn"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            Previous
          </button>
          {currentStep === NUM_BOXES - 1 ? (
            <button type="submit" className="submit-btn">
              Update
            </button>
          ) : (
            <button type="button" className="next-btn" onClick={handleNext}>
              Next
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default UpdateForm;
